/* eslint-disable prettier/prettier */
import { getPaymentMethodsOrder, getCookieByName } from 'shared/clientUtils';
import { TXN_MAP, COUNTRY } from 'types/constants';

import getState from 'store/utils/getState';
import { saleSelectors } from 'store/sale/selectors';
import { isAuthRateIncreaseHoldOut, isInBillingAddressExperiment, isBatchPaymentsEnabled, isPaymentRetryExperimentTreatmentB } from 'store/ixp/selector';
import { getFeatureEligibility, getLinkSource, getScreenPrevious } from './helpers';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { concatPaymentMethodWithSubType } from 'businessLogic/Payment/helpers';
import { initAsyncResolver, promisifyAsyncResolver } from 'shared/asyncResolver';
import { getBrowserName, isMobile } from 'server/helpers/deviceInfo';
import { isSurchargePaintedDoorOnForSale } from 'components/Shared/creditCardSurcharging/creditCardSurcharging';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'reporting/SegmentIO.js';

const OFFERING_NORMALIZATION_MAP = {
  [TXN_MAP.OFFERING_TYPES.STS]: TXN_MAP.OFFERING_TYPES.QBSE,
};

const normalizeOfferingId = (offeringId) =>
  OFFERING_NORMALIZATION_MAP[offeringId] || offeringId;

/**
 * @type {import('./SegmentIO')}
 */
const SegmentIO = {
  /**
   * @param {{transaction_type: string, domain_id: string, payment_request_id: string}} param0
   */
  getTransactionId({ transaction_type, domain_id, payment_request_id }) {
    switch (transaction_type || TXN_MAP.TYPES.INVOICE) {
      case TXN_MAP.TYPES.PAYMENT_REQUEST:
        return { domain_id: null, payment_request_id };
      case TXN_MAP.TYPES.INVOICE:
      case TXN_MAP.TYPES.ESTIMATE:
      default:
        return { domain_id, payment_request_id: null };
    }
  },

  resolveTaxonomy: initAsyncResolver(),
  getInvoiceType({ transactionType, shouldShowSchedulePay, shouldShowAutopay, subType, type }) {
    let invoiceType = transactionType;
    if (type === 'SUBSCRIPTION') {
      invoiceType = 'SUBSCRIPTION';
    } else if (shouldShowSchedulePay || shouldShowAutopay) {
      invoiceType = shouldShowAutopay ? 'AP' : 'SP';
    } else if (subType === TXN_MAP.SUB_TYPES.PAYMENT_LINK_MULTIPLE) {
      invoiceType = TXN_MAP.SUB_TYPES.PAYMENT_LINK_MULTIPLE;
    }
    return invoiceType;
  },

  setIsUserSignedIn(isUserSignedIn) {
    SegmentIO.commonData['is_user_signedIn'] = isUserSignedIn;
  },

  clickViewInvoiceEvent() {
    SegmentIO.transactionEngaged({
      ui_object: 'button',
      ui_object_detail: 'view_invoice_pdf',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
    });
  },

  clickPrivacy() {
    SegmentIO.transactionEngaged({
      ui_object: 'button',
      ui_object_detail: 'privacy_statement',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
    });
  },

  affirmBnplPromoClicked(uiAccessPoint) {
    SegmentIO.transactionEngaged({
      activity_type: 'affirm_bnpl_promo',
      ui_action: 'clicked',
      ui_object: 'link',
      ui_object_detail: 'affirm_bnpl_learn_more',
      ui_access_point: uiAccessPoint,
    });
  },
  //ACH Reporting
  clickSavePaymentMethod(is_checked) {
    SegmentIO.transactionEngaged({
      ui_object: 'checkbox',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ui_object_detail: is_checked,
    });
  },

  gratuityClicked(selected) {
    const values = ['5%', '10%', '15%', 'other', 'none'];
    SegmentIO.transactionEngaged({
      activity_type: 'tips',
      ui_object: 'radio_button',
      ui_action: 'enabled',
      ui_access_point: 'transaction_flow',
      ui_object_detail: values[selected - 1], // align to array index
    });
  },

  gratuityOtherAmount(amount) {
    SegmentIO.transactionEngaged({
      activity_type: 'tips',
      ui_object: 'form_field',
      ui_action: 'typed',
      ui_access_point: 'transaction_flow',
      ui_object_detail: amount,
    });
  },

  getInvoicingExperience(isPayable, sale) {
    if (!isPayable) {
      return 'nonPayEnabled';
    }
    if (isSurchargePaintedDoorOnForSale(getState())) {
      return 'CreditCardWithSurcharge';
    }
    if (saleSelectors.achOnlineConvenienceFeeEnabledSelector(sale)) {
      return 'ACHOnlineConvenienceFee';
    }

    return 'payEnabled';
  },

  async initTaxonomyV2(store) {
    try {
      const {
        sale,
        insight,
        companyInfo,
        auth,
        payment = {},
        ixp,
        wallet = {},
        config,
        featureFlags = {},
      } = store;

      const { txnDate, type, receivable } = sale;

      const { region, currency } = companyInfo;

      const {
        offeringId,
        token,
        isPayable,
        isPayEnabled,
        isFullyPaid,
        isPartiallyPaid,
        companyId,
        domainId,
        payorSegmentation,
      } = insight;

      const { ssrtid, schema } = config;

      checkIfSegmentScriptLoadError();

      const domain_id = domainId ? domainId.substring(domainId.indexOf(':') + 1) : null;

      const { isUserSignedIn, recipientEmail, userAgent, isBot: isBotRequest } = auth;

      const { balanceAmount } = payment;

      const { enabledPaymentMethods } = wallet;

      SegmentIO.invoice_type = type ? type.toLowerCase() : 'unknown';

      const isMobileDevice = isMobile(userAgent);
      const browserName = getBrowserName(userAgent);

      const invoiceStatus = isFullyPaid
        ? 'fully paid'
        : isPartiallyPaid
        ? 'partial Paid'
        : 'not paid';

      const invoiceDueDate = receivable ? receivable.dueDate : null;

      const payment_methods_order = getPaymentMethodsOrder({
        enabledPaymentMethods,
      })
        .map((pm) => pm.replace(',', '-'))
        .join(',');

      const test_name = ixp
        ? Object.values(ixp)
            .filter((test) => test.treatmentKey !== 'CP_CONFIG_DEFAULT')
            .map((test) => `${test.experimentId}:${test.treatmentKey}`)
            .join(',')
        : '';
      const normalizedOfferingId = normalizeOfferingId(offeringId);
      const newTaxonomyData = {
        browser_height: window.innerHeight,
        browser_width: window.innerWidth,
        // eslint-disable-next-line
        platform: navigator?.userAgentData?.platform || navigator?.platform || 'unknown',
        ivid: ssrtid,
        product_name: normalizedOfferingId,
        feature_eligibility: getFeatureEligibility(store),
        screen_previous: getScreenPrevious(),
        invoice_type: SegmentIO.invoice_type,
        object_detail: SegmentIO.invoice_type,
        company_id: companyId,
        currency: currency,
        domain_id,
        intuit_acstoken: token,
        invoice_amount: balanceAmount,
        invoice_creation_date: txnDate,
        invoice_due_date: invoiceDueDate,
        invoice_status: invoiceStatus,
        is_user_signedin: isUserSignedIn,
        is_mobile_device: isMobileDevice,
        is_bot: isBotRequest || false,
        browser_name: browserName,
        merchant_country: region,
        pay_enabled: isPayEnabled,
        payable: isPayable,
        pseudonym_id:
          schema === 'sbseg_cp_payments_prod'
            ? getCookieByName('qbn.uidp')
            : getCookieByName('qbn.ptc.uidp'),
        recipient_email: recipientEmail,
        offering_id: normalizedOfferingId,
        payment_methods_order,
        page_experience: isPayable ? 'transaction_view' : 'view_only_access', // TODO
        invoicing_experience: this.getInvoicingExperience(isPayable, sale),
        test_name,
        personalization_details: payorSegmentation,
        link_source: getLinkSource(),
      };

      SegmentIO.commonDataV2 = newTaxonomyData;
      this.resolveTaxonomy.resolve(newTaxonomyData);

      // POC: Create account group profile for B2B reporting
      if (domainId && featureFlags['SBSEG-CP-enable-analytics-group-calls']) {
        SegmentIO.group({
          groupId: domainId,
          traits: {
            group_type: SegmentIO.invoice_type,
            domain_id: domainId,
            company_id: companyId,
            region: region,
            is_pay_enabled: isPayEnabled,
          },
        });
      }
    } catch (e) {
      SegmentIO.commonDataV2 = {};
      this.resolveTaxonomy.resolve({});
      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'report',
        action: 'initTaxonomyV2',
        activityInfo: {
          status: 'error',
        },
        error: {
          stack: e.stack,
          message: e.message,
        },
      });
    }
  },

  getCommonDataV2() {
    return SegmentIO.commonDataV2;
  },
  getBaseTaxonomyProps() {
    return {
      ...SegmentIO.commonDataV2,
      org: 'sbseg',
      purpose: 'prod',
      scope: 'counterpart_portal',
      scope_area: 'checkout_&_shopping_cart',
      screen: 'cp_checkout',
      object: 'transaction',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      // POC: Add group context to all events for account-level reporting
      group_id: SegmentIO.commonDataV2?.domain_id,
    };
  },
  async resolveBaseTaxonomyProps() {
    await promisifyAsyncResolver(this.resolveTaxonomy);
    return this.getBaseTaxonomyProps();
  },
  getPaymentProps() {
    const state = getState();
    if (!state.payment) {
      return {};
    }
    const {
      sale,
      payment: {
        isDateScheduled,
        isAutoPayOn,
        gratuityValue = 0,
        amount,
        paymentMethodType,
        paymentMethodSubType,
        country,
        lastSuccessfulPaymentResponse,
      },
      wallet: { userWallets, selectedWalletId } = {},
    } = state;

    const balanceAmount = saleSelectors.balanceSelector(sale);

    const personalization_details = {
      ...(country ? { payor_country: country.a2 } : {}),
      ...SegmentIO.getBaseTaxonomyProps().personalization_details,
    };

    let invoice_status = '';
    if (lastSuccessfulPaymentResponse && lastSuccessfulPaymentResponse.amountPaid) {
      invoice_status =
        lastSuccessfulPaymentResponse.amountPaid >= sale.amount ? 'fully paid' : 'partial Paid';
    }
    const isPayPalBnpl = paymentMethodType === 'paypal_ppaam' && paymentMethodSubType === 'paylater';
    const paymentMethod = isPayPalBnpl
      ? 'pm_pp_bnpl'
      : concatPaymentMethodWithSubType(paymentMethodType, paymentMethodSubType);

    return {
      features_enabled: [
        ...(gratuityValue > 0 ? ['tips'] : []),
        ...(isDateScheduled ? ['sp'] : []),
        ...(isAutoPayOn ? ['ap'] : []),
      ],
      ...(invoice_status ? { invoice_status } : {}),
      have_pm_in_wallet:
        userWallets && userWallets.some((wallet) => wallet.id === selectedWalletId),
      payment_method: paymentMethod,
      remaining_balance: balanceAmount + gratuityValue - amount,
      schedule_active: isDateScheduled || isAutoPayOn,
      tip_amount: gratuityValue,
      payment_type: SegmentIO.invoice_type,
      personalization_details,
    };
  },

  async transactionViewed(props = {}) {
    await this.resolveBaseTaxonomyProps();

    const object_detail = isSurchargePaintedDoorOnForSale(getState())
      ? 'surchargeCC'
      : `${SegmentIO.invoice_type}`;
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|transaction:viewed:${SegmentIO.invoice_type}_transaction`,
      object_detail: object_detail,
      action: 'viewed',
      ui_object: 'page',
      ui_object_detail: 'payment_form_viewed',
      ui_action: 'loaded',
      ui_access_point: 'external_link',
      ...props,
    };

    SegmentIO.track({
      event: 'transaction:viewed',
      properties,
    });
  },

  /**
   * Helper function to determine if billing address fields are shown
   * Based on: experiment treatment + guest checkout + US country
   */
  getIsBillingAddress() {
    const state = getState();
    const isInAuthRateTreatment = isAuthRateIncreaseHoldOut(state);
    const isInBillingAddressTreatment = isInAuthRateTreatment && isInBillingAddressExperiment(state);
    const isGuestCheckout = !state.auth?.isUserSignedIn;
    const isUSCountry = state.payment?.country?.a2 === COUNTRY.US;
    return isInBillingAddressTreatment && isGuestCheckout && isUSCountry;
  },

  transactionSubmitted(props = {}) {
    const state = getState();
    const totalAmountSubmitted = state.payment?.amount || null;

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout_confirmation_page|transaction:submitted:${SegmentIO.invoice_type}_transaction`,
      action: 'submitted',
      ui_object: 'button',
      ui_object_detail: 'pay_button_clicked',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getPaymentProps(),
      ...SegmentIO.getPaymentRetryProps(),
      total_amount_submitted: totalAmountSubmitted,
      is_billing_address: SegmentIO.getIsBillingAddress(),
      ...props,
    };

    SegmentIO.track({
      event: 'transaction:submitted',
      properties,
    });
  },

  transactionSucceeded(props = {}) {
    const state = getState();
    const transactionId =
      state.payment &&
      state.payment.lastSuccessfulPaymentResponse &&
      state.payment.lastSuccessfulPaymentResponse.trackingId;
    const totalAmountCharged =
      state.payment?.lastSuccessfulPaymentResponse?.amountPaid || state.payment?.amount || null;
    // Use lastPaymentWasRetry since isPaymentRetry is reset before this is called
    const wasPaymentRetry = state.payment?.lastPaymentWasRetry || false;

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      screen_path: 'checkout_&_shopping_cart/cp_checkout_confirmation_page',
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout_confirmation_page|transaction:succeeded:${SegmentIO.invoice_type}_transaction`,
      action: 'succeeded',
      ui_object: 'page',
      ui_object_detail: 'cp_checkout_confirmation_page',
      ui_action: 'loaded',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getPaymentProps(),
      ...SegmentIO.getPaymentRetryProps(),
      transaction_id: transactionId,
      total_amount_charged: totalAmountCharged,
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_batch_payment: isBatchPaymentsEnabled(state),
      is_payment_retry: wasPaymentRetry, // Override with preserved value
      ...props,
    };

    SegmentIO.track({
      event: 'transaction:succeeded',
      properties,
    });
  },

  transactionFailed(props = {}) {
    const state = getState();
    const totalAmountFailed = state.payment?.amount || null;

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      screen_path: 'checkout_&_shopping_cart/cp_checkout_confirmation_page',
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout_confirmation_page|transaction:failed:${SegmentIO.invoice_type}_transaction`,
      action: 'failed',
      ui_object: 'page',
      ui_object_detail: 'cp_checkout_confirmation_page',
      ui_action: 'loaded',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getPaymentProps(),
      ...SegmentIO.getPaymentRetryProps(),
      total_amount_failed: totalAmountFailed,
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_batch_payment: isBatchPaymentsEnabled(state),
      ...props,
    };

    SegmentIO.track({
      event: 'transaction:failed',
      properties,
    });
  },

  transactionEngaged(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      activity_type: 'general_flow',
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|transaction:engaged:${SegmentIO.invoice_type}_transaction`,
      action: 'engaged',
      ...props,
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  componentEngaged(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      activity_type: 'general_flow',
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|transaction:engaged:${SegmentIO.invoice_type}_transaction`,
      action: 'engaged',
      object: 'component',
      ...props,
    };

    SegmentIO.track({
      event: 'component:engaged',
      properties,
    });
  },

  transactionCancelled(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      ...props,
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|transaction:canceled:${SegmentIO.invoice_type}_transaction`,
      action: 'canceled',
    };

    SegmentIO.track({
      event: 'transaction:canceled',
      properties,
    });
  },

  /**
   * This can be used to send other events that are not covered by the above methods, and needed for specific use cases.
   * @param {string} event
   * @param {Object} props
   */
  sendEvent(event, props = {}) {
    SegmentIO.track({
      event,
      properties: {
        ...SegmentIO.getBaseTaxonomyProps(),
        ...props,
      },
    });
  },

  cardFormFieldClicked(fieldName, isBillingAddress = false) {
    const store = getState();
    const fieldNameMap = {
      number: 'card_number',
      expDate: 'exp_date',
      cvc: 'cvv_code',
      name: 'name_on_card',
      zipCode: 'zip_code',
      street1: 'billing_address',
      city: 'city',
      state: 'state',
    };

    const {
      payment: { isPaymentRetry },
      ixp,
    } = store;
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const existingTestName = SegmentIO.commonDataV2?.test_name || '';
    const paymentRetryTestName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment?.experimentId}:${PaymentRetryExperiment?.treatmentKey}`
        : '';

    // Append to existing test_name if PaymentRetryExperiment is active and not already included
    const shouldAppendTestName = paymentRetryTestName && !existingTestName.includes(paymentRetryTestName);

    SegmentIO.transactionEngaged({
      ui_object: 'form_field',
      ui_object_detail: fieldNameMap[fieldName],
      is_billing_address: isBillingAddress,
      is_batch_payment: isBatchPaymentsEnabled(store),
      is_payment_retry: isPaymentRetry,
      ...(shouldAppendTestName
        ? { test_name: existingTestName ? `${existingTestName},${paymentRetryTestName}` : paymentRetryTestName }
        : {}),
    });
  },

  bankFormFieldClicked(fieldName) {
    const fieldNameMap = {
      bankCode: 'routing_number',
      accountNumber: 'account_number',
      accountNumberConfirm: 'confirm_account_number',
      name: 'account_holders_name',
    };

    SegmentIO.transactionEngaged({
      ui_object: 'form_field',
      ui_object_detail: fieldNameMap[fieldName],
    });
  },

  getCountryFieldActivityType() {
    const state = getState();
    if (!state.payment) {
      return {};
    }
    const {
      payment: { paymentMethodType },
    } = state;
    return paymentMethodType === 'cc' ? 'pm_credit' : 'pm_debit';
  },

  countryFieldClicked() {
    const state = getState();
    const {
      payment: { isPaymentRetry },
      ixp,
    } = state;
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const existingTestName = SegmentIO.commonDataV2?.test_name || '';
    const paymentRetryTestName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment?.experimentId}:${PaymentRetryExperiment?.treatmentKey}`
        : '';

    // Append to existing test_name if PaymentRetryExperiment is active and not already included
    const shouldAppendTestName = paymentRetryTestName && !existingTestName.includes(paymentRetryTestName);

    SegmentIO.transactionEngaged({
      ui_action: 'clicked',
      ui_object: 'dropdown',
      ui_object_detail: 'payor_country_options',
      ui_access_point: 'transaction_flow',
      activity_type: SegmentIO.getCountryFieldActivityType(),
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_payment_retry: isPaymentRetry || false,
      ...(shouldAppendTestName
        ? { test_name: existingTestName ? `${existingTestName},${paymentRetryTestName}` : paymentRetryTestName }
        : {}),
    });
  },

  countryFieldUpdated(country) {
    const state = getState();
    const {
      payment: { isPaymentRetry },
      ixp,
    } = state;
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const existingTestName = SegmentIO.commonDataV2?.test_name || '';
    const paymentRetryTestName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment?.experimentId}:${PaymentRetryExperiment?.treatmentKey}`
        : '';

    // Append to existing test_name if PaymentRetryExperiment is active and not already included
    const shouldAppendTestName = paymentRetryTestName && !existingTestName.includes(paymentRetryTestName);

    SegmentIO.transactionEngaged({
      ui_action: 'clicked',
      ui_object: 'dropdown',
      ui_object_detail: `payor_country:${country}`,
      ui_access_point: 'transaction_flow',
      activity_type: SegmentIO.getCountryFieldActivityType(),
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_payment_retry: isPaymentRetry || false,
      ...(shouldAppendTestName
        ? { test_name: existingTestName ? `${existingTestName},${paymentRetryTestName}` : paymentRetryTestName }
        : {}),
    });
  },

  stateFieldAutofilled(stateCode) {
    const state = getState();
    SegmentIO.transactionEngaged({
      ui_action: 'autofilled',
      ui_object: 'dropdown',
      ui_object_detail: `payor_state:${stateCode}`,
      ui_access_point: 'transaction_flow',
      activity_type: 'billing_address',
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_batch_payment: isBatchPaymentsEnabled(state),
    });
  },

  stateFieldSelected(stateCode) {
    const state = getState();
    SegmentIO.transactionEngaged({
      ui_action: 'clicked',
      ui_object: 'dropdown',
      ui_object_detail: `payor_state:${stateCode}`,
      ui_access_point: 'transaction_flow',
      activity_type: 'billing_address',
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_batch_payment: isBatchPaymentsEnabled(state),
    });
  },

  widgetViewed(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      ...SegmentIO.getPaymentProps(),
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|widget:viewed:${SegmentIO.invoice_type}_transaction`,
      object_detail: `${SegmentIO.invoice_type}`,
      action: 'viewed',
      ui_object: 'page',
      ui_object_detail: 'payment_form_viewed',
      ui_action: 'loaded',
      ui_access_point: 'external_link',
      ...props,
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },

  widgetEngaged(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      event_full: `sbseg|prod|counterpart_portal|checkout_&_shopping_cart/cp_checkout|widget:viewed:${SegmentIO.invoice_type}_transaction`,
      object_detail: `${SegmentIO.invoice_type}`,
      action: 'viewed',
      ui_object: 'page',
      ui_object_detail: 'payment_form_viewed',
      ui_action: 'loaded',
      ui_access_point: 'external_link',
      ...props,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  async frictionlessLoginShow() {
    const baseTaxonomy = await this.resolveBaseTaxonomyProps();
    const properties = {
      ...baseTaxonomy,
      object: 'widget',
      object_detail: 'frictionless_login_widget',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'viewed',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },
  async frictionlessLoginError() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_login_widget',
      action: 'failed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'viewed',
      ui_object_detail: 'identity_error',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'workflow:failed',
      properties,
    });
  },
  async frictionlessLoginSuccess(ticket, storedPaymentMethodInfo = {}) {
    const store = getState();
    await this.initTaxonomyV2(store);

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_login_widget',
      action: 'completed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form',
      ui_action: 'typed',
      ui_object_detail: 'frictionless_login_successful',
      ui_access_point: 'transaction_flow',
      ...storedPaymentMethodInfo,
    };

    SegmentIO.track({
      event: 'workflow:completed',
      properties,
    });
    if (!ticket) {
      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'report',
        action: 'track',
        activityInfo: {
          status: 'error',
        },
        error: {
          message: 'No auth ticket has been passed to frictionless login success',
        },
      });
      return;
    }
  },
  frictionlessLoginSubmit() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_login_widget',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'sure',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  frictionlessLoginCancel(source) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_login_widget',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'link',
      ui_action: 'clicked',
      ui_object_detail: `continue_as_guest_${source}`,
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  async frictionlessLoginOtpX() {
    const store = getState();
    await this.initTaxonomyV2(store);
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_login_widget',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'pop_up_close',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  frictionlessLoginNotYou() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_login_widget',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'link',
      ui_action: 'clicked',
      ui_object_detail: 'not_you',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },

  frictionlessLoginPhase3SignInCTAViewed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_phase_3',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'switch',
      ui_action: 'viewed',
      ui_object_detail: 'user_match_view',
      ui_access_point: 'card',
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },

  frictionlessLoginPhase3SignInCTAClosed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      action: 'engaged',
      object_detail: 'frictionless_phase_3',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'user_match_x',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  frictionlessLoginPhase3SignInCTALinkClicked() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_3',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'user_match_sign_in',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  frictionlessLoginPhase3WidgetSignInCompleted(storedPaymentMethodInfo = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_experience',
      action: 'success',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'form',
      ui_action: 'viewed',
      ui_object_detail: 'sign_in_success',
      ui_access_point: 'transaction_flow',
      ...storedPaymentMethodInfo,
    };
    SegmentIO.track({
      event: 'transaction:succeeded',
      properties,
    });
  },

  frictionlessLoginPhase3WidgetSignInFailed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_experience',
      action: 'fail',
      scope_area: 'checkout_&_shopping_cart',
      ui_object: 'form',
      ui_action: 'viewed',
      ui_object_detail: 'sign_in_fail',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: 'transaction:failed',
      properties,
    });
  },

  frictionlessExperienceUsernameAvailabilityReqToIdentity() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_experience',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      ui_object_detail: 'cp_to_identity_request',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: 'workflow:engaged',
      properties,
    });
  },

  frictionlessLoginPhase3EligibilityCheck(status) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_experience',
      action: status,
      scope_area: 'checkout_&_shopping_cart',
      ui_object_detail: 'fc_eligibility_view',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: `workflow:${status}`,
      properties,
    });
  },

  frictionlessCheckoutToggle(detail) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'switch',
      ui_action: detail === 'on' ? 'enabled' : 'disabled',
      ui_object_detail: `user_turned_${detail}_save_info`,
      ui_access_point: 'transaction_flow',
    };

    if (SegmentIO.commonDataV2 && Array.isArray(SegmentIO.commonDataV2.feature_eligibility)) {
      // Create a new array to avoid mutating the original
      let updatedFeatureEligibility = [...SegmentIO.commonDataV2.feature_eligibility];

      if (detail === 'on') {
        // Add the feature flag if it doesn't already exist
        if (!updatedFeatureEligibility.includes('is_frictionless_signup_eligible')) {
          updatedFeatureEligibility.push('is_frictionless_signup_eligible');
        }
      } else {
        // Remove the feature flag if it exists
        updatedFeatureEligibility = updatedFeatureEligibility.filter(
          (item) => item !== 'is_frictionless_signup_eligible'
        );
      }

      // Update the commonDataV2 object with the new array
      SegmentIO.commonDataV2 = {
        ...SegmentIO.commonDataV2,
        feature_eligibility: updatedFeatureEligibility,
      };
    }

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  frictionlessCheckoutEmailEdit() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form_field',
      ui_action: 'typed',
      ui_object_detail: 'user_edited_email_id',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },
  frictionlessCheckoutPhoneEdit() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form_field',
      ui_action: 'typed',
      ui_object_detail: 'user_edited_phone_no',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  async frictionlessCheckoutSuccess(storedPaymentMethodInfo = {}) {
    const store = getState();
    await this.initTaxonomyV2(store);

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_phase_2',
      action: 'completed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form',
      ui_action: 'typed',
      ui_object_detail: 'creation_succeeded',
      ui_access_point: 'transaction_flow',
      ...storedPaymentMethodInfo,
    };

    SegmentIO.track({
      event: 'workflow:completed',
      properties,
    });
  },

  frictionlessCheckoutError() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_phase_2',
      action: 'failed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'disabled',
      ui_object_detail: 'identity_error',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'workflow:failed',
      properties,
    });
  },
  frictionlessCheckoutOtpX() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'pop_up_close',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  frictionlessCheckoutAccountExists() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'frictionless_phase_2',
      action: 'failed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'disabled',
      ui_object_detail: 'account_exists',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'workflow:failed',
      properties,
    });
  },

  // New frictionless subscription events
  frictionlessSubscriptionEmailFieldViewed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form_field',
      ui_action: 'viewed',
      ui_object_detail: 'email_field_viewed',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  frictionlessSubscriptionPhoneFieldViewed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'form_field',
      ui_action: 'viewed',
      ui_object_detail: 'phone_field_viewed',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  frictionlessSubscriptionPhoneFieldGuidanceShown() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'frictionless_phase_2',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'banner',
      ui_action: 'viewed',
      ui_object_detail: 'phone_field_guidance_shown',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  cardOnFileSaveMerchantWalletCheckboxViewed() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      ui_object_detail: 'save_merchant_wallet_viewed',
      object: 'transaction',
      object_detail: 'card_on_file_ff',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'viewed',
      ui_access_point: 'transaction_flow',
    };
    SegmentIO.track({
      event: 'transaction:viewed',
      properties,
    });
  },

  cardOnFileSaveMerchantWalletsInfo(screen, status) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      ui_object_detail: 'saved_merchant_wallets_info',
      ui_object: 'modal',
      screen: screen,
      ui_action: 'viewed',
      object_detail: 'saved_merchant_wallets',
      ui_access_point: 'transaction_flow',
      action: status,
    };
    SegmentIO.track({
      event: `transaction:${status}`,
      properties,
    });
  },

  successScreenModernTemplateViewed() {
    const state = getState();
    const totalAmountCharged =
      state.payment?.lastSuccessfulPaymentResponse?.amountPaid || state.payment?.amount || null;
    // Use lastPaymentWasRetry since isPaymentRetry is reset before this is called
    const wasPaymentRetry = state.payment?.lastPaymentWasRetry || false;

    // Get complete screen tracking props for additional context
    const completeScreenProps = SegmentIO.getCompleteScreenTrackingProps();

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'invoice',
      action: 'succeeded',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'page',
      ui_action: 'loaded',
      ui_access_point: 'transaction_flow',
      ui_object_detail: 'cp_checkout_confirmation_modern_page',
      ...SegmentIO.getPaymentProps(),
      total_amount_charged: totalAmountCharged,
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_payment_retry: wasPaymentRetry,
      // Include additional tracking props from complete screen (test_name, is_batch_payment, etc.)
      // but keep is_payment_retry from wasPaymentWasRetry as it's more accurate at this point
      ...(completeScreenProps.test_name ? { test_name: completeScreenProps.test_name } : {}),
      ...(completeScreenProps.is_batch_payment !== undefined
        ? { is_batch_payment: completeScreenProps.is_batch_payment }
        : {}),
      ...(completeScreenProps.currency ? { currency: completeScreenProps.currency } : {}),
      ...(completeScreenProps.payment_method ? { payment_method: completeScreenProps.payment_method } : {}),
    };
    SegmentIO.track({
      event: 'transaction:succeeded',
      properties,
    });
  },

  successScreenModernTemplateDownloadConfirmationClicked() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'transaction',
      object_detail: 'download_confirmation_link',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'cp_checkout_download_confirmation_link',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getCompleteScreenTrackingProps(),
    };
    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },
  bulkPaymentsInvoiceNumberClicked(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'bulk_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'link',
      ui_action: 'clicked',
      ui_object_detail: 'invoice_number_clicked',
      ui_access_point: 'modal',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  bulkPaymentsModalViewed(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'bulk_payments',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'viewed',
      ui_object_detail: 'modal_opened',
      ui_access_point: 'modal',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },
  bulkPaymentsModalContinueToCheckout(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'bulk_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'clicked',
      ui_object_detail: 'continue_to_checkout_clicked',
      ui_access_point: 'modal',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  bulkPaymentsModalClosed(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'bulk_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'clicked',
      ui_object_detail: 'modal_closed',
      ui_access_point: 'modal',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  bulkPaymentsSelectInvoicesToPay(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'bulk_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'clicked',
      ui_object_detail: 'select_invoices_to_pay_clicked',
      ui_access_point: 'modal',
      ...params,
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },
  aiAssistViewed(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'ai_assist',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'widget',
      ui_action: 'viewed',
      ui_object_detail: 'ai_assist_viewed',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'transaction:viewed',
      properties,
    });
  },
  aiAssistChatOpened(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'modal',
      object_detail: 'chat',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'viewed',
      ui_object_detail: 'chat_opened',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },
  aiAssistQuestionOpened(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'ai_assist',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'accordion',
      ui_action: 'clicked',
      ui_object_detail: `question_${params.questionNumber}`,
      ui_access_point: 'transaction_flow',
      ...params,
    };
    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },
  aiAssistMessageSentModal(params) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'workflow',
      object_detail: 'ai_assist',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'modal',
      ui_action: 'clicked',
      ui_object_detail: 'msg_sent',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },
  /**
   * Batch Payments - Table Viewed
   * Tracks when the batch payments table is displayed to the user
   * @param {Object} params - Analytics parameters
   * @param {Array} params.invoiceList - Full array of invoice objects
   * @param {number} params.totalInvoicesCount - Total count of invoices shown
   * @param {boolean} params.isSourceBatchPayment - Whether source parameter is 'batchPayment'
   * @param {string} params.customer_id - Customer/Name ID (c2) for experiment tracking
   */
  async batchPaymentsTableViewed(params) {
    // Wait for taxonomy to be initialized to ensure company_id etc are available
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      feature_eligibility: [...(baseProps.feature_eligibility || []), 'batch_payments'],
      object: 'widget',
      object_detail: 'batch_payments',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'table',
      ui_object_detail: 'batch_invoices_table_viewed',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },
  /**
   * Batch Payments - Invoice Selected
   * Tracks when a user selects/deselects invoices in the batch payments table
   * @param {Object} params - Analytics parameters
   * @param {number} params.totalInvoicesSelected - Count of currently selected invoices
   * @param {Array} params.selectedInvoicesList - Full array of selected invoice objects
   * @param {string} params.customer_id - Customer/Name ID (c2) for experiment tracking
   */
  async batchPaymentsInvoiceSelected(params) {
    // Wait for taxonomy to be initialized to ensure company_id etc are available
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      feature_eligibility: [...(baseProps.feature_eligibility || []), 'batch_payments'],
      object: 'widget',
      object_detail: 'batch_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'checkbox',
      ui_object_detail: 'invoice_selected',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  /**
   * Batch Payments - Default Invoice Deselected
   * Tracks when a user specifically deselects the default invoice
   * @param {Object} params - Analytics parameters
   * @param {string} params.defaultInvoiceNumber - The invoice number of the default invoice
   * @param {number} params.remainingSelectedCount - Count of remaining selected invoices
   * @param {string} params.customer_id - Customer/Name ID (c2) for experiment tracking
   */
  async batchPaymentsDefaultInvoiceDeselected(params) {
    // Wait for taxonomy to be initialized to ensure company_id etc are available
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      feature_eligibility: [...(baseProps.feature_eligibility || []), 'batch_payments'],
      object: 'widget',
      object_detail: 'batch_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'checkbox',
      ui_object_detail: 'default_invoice_deselected',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  /**
   * Batch Payments - Download Confirmation Clicked
   * Tracks when a user clicks the download confirmation button on the complete screen
   * @param {Object} params - Analytics parameters
   * @param {number} params.totalInvoicesPaid - Total number of invoices paid
   * @param {number} params.totalAmountCharged - Total amount charged
   * @param {string} params.currency - Currency used
   * @param {string} params.paymentMethod - Payment method used
   * @param {string} params.customer_id - Customer/Name ID (c2) for experiment tracking
   */
  async batchPaymentsDownloadConfirmationClicked(params) {
    // Wait for taxonomy to be initialized to ensure company_id etc are available
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      feature_eligibility: [...(baseProps.feature_eligibility || []), 'batch_payments'],
      object: 'widget',
      object_detail: 'batch_payments',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_object_detail: 'download_confirmation',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },
  /**
   * Batch Payments - Complete Screen Viewed
   * Tracks when the batch payment complete screen is viewed
   * @param {Object} params - Analytics parameters
   * @param {Array} params.successfulInvoicesList - Full array of successful invoice objects
   * @param {number} params.successfulInvoicesCount - Count of successful invoices
   * @param {Array} params.failedInvoicesList - Full array of failed invoice objects
   * @param {number} params.failedInvoicesCount - Count of failed invoices
   * @param {number} params.totalAmountCharged - Total amount charged
   * @param {string} params.currency - Currency used
   * @param {string} params.paymentMethod - Payment method used
   * @param {string} params.customer_id - Customer/Name ID (c2) for experiment tracking
   */
  async batchPaymentsCompleteScreenViewed(params) {
    // Wait for taxonomy to be initialized to ensure company_id etc are available
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      feature_eligibility: [...(baseProps.feature_eligibility || []), 'batch_payments'],
      object: 'widget',
      object_detail: 'batch_payments',
      action: 'viewed',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'viewed',
      ui_object_detail: 'batch_payment_complete_screen_viewed',
      ui_access_point: 'transaction_flow',
      ...params,
    };

    SegmentIO.track({
      event: 'widget:viewed',
      properties,
    });
  },

  /**
   * Track experiment:mapped event when user gets bucketed into experiment
   */
  experimentMapped(experimentId, treatmentKey) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'experiment',
      object_detail: experimentId,
      action: 'mapped',
      variation_name: treatmentKey,
    };

    SegmentIO.track({
      event: 'experiment:mapped',
      properties,
    });
  },

  /**
   * Track content:engaged event for user interactions with content
   */
  contentEngaged(props = {}) {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      action: 'engaged',
      ...props,
    };

    SegmentIO.track({
      event: 'content:engaged',
      properties,
    });
  },

  /**
   * Track web performance metrics (TTFB, LCP, FCP)
   * @param {Object} params - Analytics parameters
   * @param {string} params.metricType - Type of metric: 'TTFB', 'LCP', or 'FCP'
   * @param {number} params.value - The metric value in milliseconds
   */
  trackWebPerformanceMetric({ metricType, value }) {
    const validMetricTypes = ['TTFB', 'LCP', 'FCP'];
    if (!validMetricTypes.includes(metricType) || typeof value !== 'number') {
      return;
    }

    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'performance',
      object_detail: 'web_vitals',
      action: 'measured',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'metric',
      ui_object_detail: metricType,
      ui_action: 'recorded',
      ui_access_point: 'transaction_flow',
      metric_type: metricType,
      metric_value: value,
    };

    SegmentIO.track({
      event: 'performance:measured',
      properties,
    });
  },

  /**
   * Track manage payment methods screen view (COF)
   */
  async manageScreenViewed() {
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      object: 'transaction',
      action: 'succeeded',
      object_detail: 'manage_screen',
      ui_object: 'page',
      ui_object_detail: 'manage_screen_view',
      ui_action: 'loaded',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:succeeded',
      properties,
    });
  },

  /**
   * Track manage payment methods error screen view (COF)
   */
  async manageScreenErrorViewed() {
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      object: 'transaction',
      action: 'failed',
      object_detail: 'manage_screen',
      ui_object: 'page',
      ui_object_detail: 'manage_screen_error_view',
      ui_action: 'loaded',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:failed',
      properties,
    });
  },

  /**
   * Track delete wallet button click on manage payment methods screen (COF)
   */
  async manageScreenDeleteClicked() {
    const baseProps = await SegmentIO.resolveBaseTaxonomyProps();
    const properties = {
      ...baseProps,
      object: 'transaction',
      action: 'engaged',
      object_detail: 'manage_screen',
      ui_object: 'button',
      ui_object_detail: 'manage_screen_wallet_delete_cta',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
    };

    SegmentIO.track({
      event: 'transaction:engaged',
      properties,
    });
  },

  /**
   * Complete Screen Invoice Link Clicked
   * Tracks when user clicks invoice link from complete screen
   */
  completeScreenInvoiceLinkClicked() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'complete_screen',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'link',
      ui_action: 'clicked',
      ui_object_detail: 'invoice_link_clicked',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getCompleteScreenTrackingProps(),
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },

  /**
   * Complete Screen Make Another Payment Clicked
   * Tracks when user clicks Make Another Payment button from complete screen
   */
  completeScreenMakeAnotherPaymentClicked() {
    const properties = {
      ...SegmentIO.getBaseTaxonomyProps(),
      object: 'widget',
      object_detail: 'complete_screen',
      action: 'engaged',
      scope_area: 'checkout_&_shopping_cart',
      screen_path: 'checkout_&_shopping_cart/cp_checkout',
      ui_object: 'button',
      ui_action: 'clicked',
      ui_object_detail: 'make_another_payment',
      ui_access_point: 'transaction_flow',
      ...SegmentIO.getCompleteScreenTrackingProps(),
    };

    SegmentIO.track({
      event: 'widget:engaged',
      properties,
    });
  },

  /**
   * Facade for Segment.IO Track
   */
  track({ event, properties }) {
    try {
      typeof window === 'object' &&
        window.analytics &&
        window.analytics.track &&
        window.analytics.track(event, properties, {
          context: { tracking_application_name: properties.tracking_application_name },
        });
    } catch (e) {
      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'report',
        action: 'track',
        activityInfo: {
          status: 'error',
        },
        error: {
          stack: e.stack,
          message: e.message,
        },
      });
    }
  },

  /**
   * Facade for Segment.IO Group - Creates account profiles for B2B reporting
   * @param {string} groupId - Account identifier (domain_id)
   * @param {Object} traits - Account properties
   */
  group({ groupId, traits = {} }) {
    try {
      typeof window === 'object' &&
        window.analytics &&
        window.analytics.group &&
        window.analytics.group(groupId, traits, {
          context: { tracking_application_name: 'cp-client' },
        });
    } catch (e) {
      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'report',
        action: 'group',
        activityInfo: {
          status: 'error',
        },
        error: {
          stack: e.stack,
          message: e.message,
        },
      });
    }
  },
  openInvoicesHelpers: {
    getTotalInvoiceAmountView: (openInvoices, balanceAmount) => {
      return openInvoices
        ? openInvoices.reduce(
            (initialValue, invoice) => initialValue + invoice.balance,
            balanceAmount
          )
        : balanceAmount;
    },
    getInvoiceDueDate: (openInvoices, invoiceDueDate) => {
      return openInvoices && openInvoices.length > 0
        ? openInvoices.reduce(
            (initialValue, invoice) => [...initialValue, invoice.dueDate],
            [invoiceDueDate]
          )
        : invoiceDueDate;
    },
    getInvoiceAmount: (openInvoices, amount) => {
      return openInvoices && openInvoices.length > 0
        ? openInvoices.reduce(
            (initialValue, invoice) => [...initialValue, invoice.totalAmt],
            [amount]
          )
        : amount;
    },
  },

  // ============================================
  // Payment Retry Events
  // ============================================

  /**
   * Get payment retry properties from store
   * @returns {Object} Payment retry properties
   */
  getPaymentRetryProps() {
    const state = getState();
    if (!state.payment) {
      return {
        is_payment_retry: false,
        original_total_amount: null,
        original_payment_method: null,
      };
    }
    const {
      payment: { isPaymentRetry, originalPaymentAmount, originalPaymentMethod },
      ixp,
    } = state;
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const existingTestName = SegmentIO.commonDataV2?.test_name || '';
    const paymentRetryTestName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment?.experimentId}:${PaymentRetryExperiment?.treatmentKey}`
        : '';

    // Append to existing test_name if PaymentRetryExperiment is active and not already included
    const shouldAppendTestName = paymentRetryTestName && !existingTestName.includes(paymentRetryTestName);

    return {
      is_payment_retry: isPaymentRetry || false,
      original_total_amount: originalPaymentAmount || null,
      original_payment_method: originalPaymentMethod || null,
      ...(shouldAppendTestName
        ? { test_name: existingTestName ? `${existingTestName},${paymentRetryTestName}` : paymentRetryTestName }
        : {}),
    };
  },

  /**
   * Get complete screen tracking properties from store
   * @returns {Object} Complete screen tracking properties
   */
  getCompleteScreenTrackingProps() {
    const state = getState();
    if (!state.payment) {
      return {
        is_payment_retry: false,
        is_batch_payment: false,
        is_billing_address: false,
        total_amount_charged: null,
        currency: null,
        payment_method: null,
      };
    }

    const {
      payment: {
        paymentMethodType,
        paymentMethodSubType,
        lastSuccessfulPaymentResponse,
      },
      sale,
      companyInfo,
      ixp,
    } = state;

    // Determine if in payment retry experiment TreatmentB
    const isInPaymentRetryTreatmentB = isPaymentRetryExperimentTreatmentB(state);
    const PaymentRetryExperiment = ixp?.PaymentRetryExperiment;
    const existingTestName = SegmentIO.commonDataV2?.test_name || '';
    const paymentRetryTestName =
      PaymentRetryExperiment?.experimentKey === 'IXP2_541549'
        ? `${PaymentRetryExperiment?.experimentId}:${PaymentRetryExperiment?.treatmentKey}`
        : '';

    // Append to existing test_name if PaymentRetryExperiment is active and not already included
    const shouldAppendTestName = paymentRetryTestName && !existingTestName.includes(paymentRetryTestName);
    const testName = shouldAppendTestName
      ? existingTestName
        ? `${existingTestName},${paymentRetryTestName}`
        : paymentRetryTestName
      : undefined;

    // Get total amount charged
    const totalAmountCharged =
      lastSuccessfulPaymentResponse?.amountPaid || state.payment?.amount || null;

    // Get currency from companyInfo or sale
    const currency = companyInfo?.currency || sale?.currency || null;

    // Construct payment method similar to getPaymentProps
    const isPayPalBnpl = paymentMethodType === 'paypal_ppaam' && paymentMethodSubType === 'paylater';
    const paymentMethod = paymentMethodType && paymentMethodSubType
      ? isPayPalBnpl
        ? 'pm_pp_bnpl'
        : concatPaymentMethodWithSubType(paymentMethodType, paymentMethodSubType)
      : null;

    return {
      is_payment_retry: isInPaymentRetryTreatmentB || false,
      ...(testName ? { test_name: testName } : {}),
      is_batch_payment: isBatchPaymentsEnabled(state) || false,
      is_billing_address: SegmentIO.getIsBillingAddress() || false,
      ...(totalAmountCharged !== null ? { total_amount_charged: totalAmountCharged } : {}),
      ...(currency !== null ? { currency } : {}),
      ...(paymentMethod !== null ? { payment_method: paymentMethod } : {}),
    };
  },

  /**
   * Track when payment retry workflow edit amount box is viewed
   * Event: widget:viewed
   */
  paymentRetryWorkflowAmtBoxViewed() {
    SegmentIO.widgetViewed({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'retry_workflow',
      ui_object: 'banner',
      ui_object_detail: 'retry_workflow_box_viewed',
      ui_access_point: 'transaction_flow',
    });
  },

  /**
  * Track when payment retry workflow error banner is viewed
  * Event: widget:viewed
  */
  paymentRetryWorkflowErrorViewed() {
    SegmentIO.widgetViewed({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'retry_workflow',
      ui_object: 'banner',
      ui_object_detail: 'retry_workflow_error_viewed',
      ui_access_point: 'transaction_flow',
    });
  },

  /**
   * Track when payment retry error banner is viewed
   * Event: widget:viewed
   */
  paymentRetryErrorViewed(props = {}) {
    SegmentIO.widgetViewed({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'payment_retry',
      ui_object: 'banner',
      ui_object_detail: 'payment_retry_error_viewed',
      ui_access_point: 'transaction_flow',
      ...props,
    });
  },

  /**
   * Track when payment retry workflow reset button is clicked
   * Event: widget:engaged
   */
  paymentRetryWorkflowResetClicked() {
    SegmentIO.widgetEngaged({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'retry_workflow',
      ui_object: 'button',
      ui_object_detail: 'retry_workflow_reset',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
    });
  },

  /**
   * Track when payment retry workflow reset button is clicked
   * Event: widget:engaged
   */
    paymentRetryWorkflowAmtBtnClicked(amt) {
      SegmentIO.widgetEngaged({
        ...SegmentIO.getPaymentRetryProps(),
        object: 'widget',
        object_detail: 'retry_workflow',
        ui_object: 'button',
        ui_object_detail: `retry_workflow:${amt}`,
        ui_action: 'clicked',
        ui_access_point: 'transaction_flow',
      });
    },

  /**
   * Track when billing info error banner is viewed
   * Event: widget:viewed
   */
  billingInfoErrorViewed(props = {}) {
    const state = getState();
    const totalAmountFailed = state.payment?.amount || null;
    SegmentIO.widgetViewed({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'decline',
      ui_object: 'banner',
      ui_object_detail: 'decline_error_viewed',
      ui_access_point: 'transaction_flow',
      is_billing_address: SegmentIO.getIsBillingAddress(),
      is_batch_payment: isBatchPaymentsEnabled(state),
      total_amount_failed: totalAmountFailed,
      ...props,
    });
  },

  /**
   * Track when user clicks on a payment retry message/CTA
   * Event: widget:engaged
   * @param {string} messageSelected - Which message/CTA was selected
   */
  paymentRetryMessageSelected(messageSelected, props = {}) {
    SegmentIO.widgetEngaged({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'payment_retry',
      action: 'engaged',
      ui_object: 'link',
      ui_object_detail: 'payment_retry_error_selected',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      message_selected: messageSelected,
      ...props,
    });
  },

  /**
   * Track when user clicks to edit payment amount
   * Event: widget:engaged
   */
  editAmountSelected(props = {}) {
    SegmentIO.widgetEngaged({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'edit_amount',
      action: 'engaged',
      ui_object: 'link',
      ui_object_detail: 'edit_amount_selected',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...props,
    });
  },

  /**
   * Track when user clicks "Pay another amount" button (TreatmentB credit floor)
   * Event: widget:engaged
   */
  payOtherAmountSelected(props = {}) {
    SegmentIO.widgetEngaged({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'pay_other_amount',
      action: 'engaged',
      ui_object: 'link',
      ui_object_detail: 'pay_other_amount_selected',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ...props,
    });
  },

  /**
   * Track when user submits a new payment amount
   * Event: widget:engaged
   * @param {number} newTotalAmount - The new amount user submitted
   */
  editAmountSubmitted(newTotalAmount, props = {}) {
    SegmentIO.widgetEngaged({
      ...SegmentIO.getPaymentRetryProps(),
      object: 'widget',
      object_detail: 'edit_amount',
      action: 'engaged',
      ui_object: 'form',
      ui_object_detail: 'edit_amount_submitted',
      ui_action: 'submitted',
      ui_access_point: 'transaction_flow',
      new_total_amount: newTotalAmount,
      ...props,
    });
  },

  _buildPlaidEventProps(metadata = {}, domainId, recipientEmail) {
    return {
      object_detail: 'plaid_link_widget',
      ui_object: 'widget',
      ui_access_point: 'transaction_flow',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
      link_session_id: metadata?.link_session_id,
      request_id: metadata?.request_id,
      error_type: metadata?.error_type,
      error_code: metadata?.error_code,
      error_message: metadata?.error_message,
      view_name: metadata?.view_name,
      mfa_type: metadata?.mfa_type,
    };
  },

  plaidLinkOpened(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_link_opened',
      ui_object_detail: 'plaid_link',
    });
  },

  plaidInstitutionSelected(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_institution_selected',
      ui_object_detail: 'institution_select',
      institution_name: metadata?.institution_name,
      institution_id: metadata?.institution_id,
      institution_search_query: metadata?.institution_search_query,
      transfer_status: metadata?.transfer_status,
    });
  },

  plaidCredentialsSubmitted(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_credentials_submitted',
      ui_object_detail: 'bank_credentials',
      institution_id: metadata?.institution_id,
      institution_name: metadata?.institution_name,
      institution_search_query: metadata?.institution_search_query,
    });
  },

  plaidHandoff(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_handoff',
      ui_object_detail: 'plaid_link_handoff',
      institution_id: metadata?.institution_id,
      institution_name: metadata?.institution_name,
      institution_search_query: metadata?.institution_search_query,
    });
  },

  plaidExit(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_exit',
      ui_object_detail: 'plaid_link_exit',
      status: metadata?.status,
      institution_name: metadata?.institution?.name,
      institution_id: metadata?.institution?.institution_id,
      transfer_status: metadata?.transfer_status,
    });
  },

  plaidSuccess(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_success',
      ui_object_detail: 'plaid_link_success',
      institution_id: metadata?.institution?.institution_id,
      institution_name: metadata?.institution?.name,
      transfer_status: metadata?.transfer_status,
      account_subtype: metadata?.account?.subtype,
      account_type: metadata?.account?.type,
    });
  },

  plaidError(metadata, domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ...SegmentIO._buildPlaidEventProps(metadata, domainId, recipientEmail),
      ui_action: 'plaid_error',
      ui_object_detail: 'plaid_link_flow_error',
      error_type: metadata?.error_type,
      error_code: metadata?.error_code,
      error_message: metadata?.error_message,
    });
  },

  plaidControl(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_show_control_experience',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'control',
    });
  },

  plaidFailureFallbackToControl(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_show_control_experience_fallback_to_control',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
    });
  },

  plaidErrorScreenViewed(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_error_screen_viewed',
      ui_object_detail: 'plaid_error_screen',
      object_detail: 'plaid_error_screen',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
    });
  },

  plaidInstantTabsViewed(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_instant_tabs_viewed',
      ui_object_detail: 'plaid_instant_tabs',
      object_detail: 'plaid_instant_tabs',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
    });
  },

  plaidManualEntryViewed(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_bank_transfer_viewed',
      ui_object_detail: 'bank_transfer',
      object_detail: 'plaid_link_widget',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
    });
  },

  plaidErrorScreenManualEntryClicked(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_error_screen_bank_transfer_manual_entry_clicked',
      ui_object_detail: 'plaid_error_screen_bank_transfer_manual_entry_btn',
      object_detail: 'plaid_error_screen',
      ui_object: 'widget',
      domain_id: domainId,
      recipient_email: recipientEmail,
      exp_variant: 'plaid',
    });
  },

  plaidRetryTokenExchange(domainId, recipientEmail) {
    SegmentIO.componentEngaged({
      ui_action: 'plaid_retry_token_exchange',
      ui_object_detail: 'plaid_retry_token_exchange_btn',
      domain_id: domainId,
      recipient_email: recipientEmail,
      object_detail: 'plaid_error_screen',
      ui_object: 'widget',
      exp_variant: 'plaid',
    });
  },

  /**
   * Track Plaid payment submitted event.
   * @param {string} [domainId] - Domain identifier (e.g. INVOICE:realmId_localId)
   * @param {string} [recipientEmail] - Recipient email address
   */
  plaidPaymentSubmitted(domainId, recipientEmail) {
    SegmentIO.track({
      event: 'transaction:submitted',
      properties: {
        ui_action: 'plaid_payment_submitted',
        ui_object_detail: 'plaid_payment_submitted_btn',
        object_detail: 'plaid_payment_submitted',
        ui_object: 'widget',
        domain_id: domainId,
        recipient_email: recipientEmail,
        exp_variant: 'plaid',
      },
    });
  },

  trackPlaidEvent(eventName, metadata = {}, domainId, recipientEmail) {
    switch (eventName) {
      case 'OPEN':
        SegmentIO.plaidLinkOpened(metadata, domainId, recipientEmail);
        break;
      case 'SELECT_INSTITUTION':
        SegmentIO.plaidInstitutionSelected(metadata, domainId, recipientEmail);
        break;
      case 'SUBMIT_CREDENTIALS':
        SegmentIO.plaidCredentialsSubmitted(metadata, domainId, recipientEmail);
        break;
      case 'HANDOFF':
        SegmentIO.plaidHandoff(metadata, domainId, recipientEmail);
        break;
      case 'EXIT':
        SegmentIO.plaidExit(metadata, domainId, recipientEmail);
        break;
      case 'SUCCESS':
        SegmentIO.plaidSuccess(metadata, domainId, recipientEmail);
        break;
      case 'CONTROL':
        SegmentIO.plaidControl(domainId, recipientEmail);
        break;
      case 'ERROR':
        SegmentIO.plaidError(metadata, domainId, recipientEmail);
        break;
      case 'MANUAL_ENTRY_VIEWED':
        SegmentIO.plaidManualEntryViewed(domainId, recipientEmail);
        break;
      default:
        break;
    }
  },
};

async function checkIfSegmentScriptLoadError() {
  try {
    // sending splunk log to track failings of segmentIO script loading
    if (typeof window === 'object') {
      const errorType = !window.analytics
        ? 'missing script'
        : window.analyticsError
        ? 'analytics error'
        : !window.analytics.track
        ? 'missing track method'
        : false;

      if (errorType) {
        const analyticsError = window.analyticsError || {};
        if (!analyticsError.message) {
          return;
        }
        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'report',
          action: 'catchAnalyticsError',
          activityInfo: {
            status: 'error',
          },
          error: {
            message: JSON.stringify(analyticsError),
          },
        });
      }
    }
  } catch (e) {
    return;
  }
}

export default SegmentIO;
